import { t } from '@lingui/macro'
import { useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { Card } from '@/@bothrs-ui/web/components/card/Card'
import { Flex, FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { viteEnvironment } from '@/config/environment'
import { breakpoints } from '@/config/layout'
import { mixpanel } from '@/mixpanel'
import { useServers } from '@/modules/healthblocks/ServerProvider'
import { usePersistedTenant } from '@/modules/shared/hooks/usePersistedTenant'
import { useQaClick } from '@/utils/qa-mode'
import { AuthLayout } from '../../components/auth-layout/AuthLayout'
import { StBigLogo } from './Landing.page.styled'

/**
 * This component is used to select a hospital from a list of servers. And to welcome the user to the platform.
 */
export const LandingPage = () => {
  const image = '/img/happy-patient.jpg'
  const servers = useServers()
  const handleTitleClick = useQaClick()
  const navigate = useNavigate()
  const location = useLocation()
  const { persistedTenant, isHydrated } = usePersistedTenant()
  const showPicker = location.pathname === '/select-hospital'

  useEffect(() => {
    if (showPicker) {
      mixpanel.track('choose_hospital')
    }
  }, [showPicker])

  useEffect(() => {
    if (showPicker || !isHydrated) return

    if (persistedTenant) {
      navigate(`/${persistedTenant}`, { replace: true })
    } else {
      navigate('/azmm', { replace: true })
    }
  }, [showPicker, isHydrated, persistedTenant, navigate])

  return (
    <AuthLayout HeaderComponent={<StBigLogo src='/img/logo.png' alt='MijnZiekenhuis Logo' />} image={image}>
      {/* Title */}
      <StTitle onClick={handleTitleClick}>
        {t({
          id: 'landing.header',
          message: 'Welkom bij MijnZiekenhuis',
        })}
      </StTitle>

      {/* Body */}
      {t({
        id: 'landing.description',
        message:
          'Beheer vlot je agenda, consulteer je facturen en meer. Meld je aan en ontdek alle functionaliteiten! MijnZiekenhuis is beschikbaar via computer of smartphone/tablet.',
      })
        .split('\n')
        .map((line) => (
          <StParagraph key={line}>{line}</StParagraph>
        ))}

      {/* Hospital selection */}
      <FlexColumn>
        <StHospitalSelectionTitle>
          {t({
            id: 'landing.hospitalSelection',
            message: 'Bij welk ziekenhuis wil je aanmelden?',
          })}
        </StHospitalSelectionTitle>

        <StCardsContainer>
          {servers.map((server) => (
            <StHospitalLink key={server.id} to={`/${server.tenant}`}>
              <StHospitalCard environment={server.environment ?? 'prd'}>
                <StHospitalCardImage src={server.logo} alt={server.display} />

                {viteEnvironment !== server.environment && (
                  <StEnvironmentLabel environment={server.environment ?? 'prd'}>
                    {server.environment}
                  </StEnvironmentLabel>
                )}
              </StHospitalCard>
            </StHospitalLink>
          ))}
        </StCardsContainer>
      </FlexColumn>
    </AuthLayout>
  )
}

// Styled Components
// ------------------------------------------------------------------------- /

const StTitle = styled.h1`
  magin: 0;

  font-size: ${({ theme }) => theme.Typography.Heading0['font-size']};
  line-height: ${({ theme }) => theme.Typography.Heading0['line-height']};
  font-weight: ${({ theme }) => theme.Typography.Heading0['font-weight']};
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};

  // Mobile
  @media ${breakpoints.toS} {
    font-size: ${({ theme }) => theme.Typography.Heading1['font-size']};
    line-height: ${({ theme }) => theme.Typography.Heading1['line-height']};
    font-weight: ${({ theme }) => theme.Typography.Heading1['font-weight']};
  }
`

const StParagraph = styled.p`
  font-size: ${({ theme }) => theme.Typography.BodyLargeRegular['font-size']};
  line-height: ${({ theme }) => theme.Typography.BodyLargeRegular['line-height']};
  font-weight: ${({ theme }) => theme.Typography.BodyLargeRegular['font-weight']};
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
`

const StCardsContainer = styled(Flex)`
  gap: ${({ theme }) => theme.UI.SpacingPx.Space6};
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: stretch;

  width: 100%;
  max-width: 100%;
`

const StHospitalLink = styled(Link)`
  @media ${breakpoints.toS} {
    flex: 1;
    min-width: 0;
  }
`

const StHospitalCard = styled(Card)<{
  environment: 'local' | 'dev' | 'acc' | 'prd' | 'qa'
}>`
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4};
  text-align: center;
  width: 240px;
  min-height: 120px;

  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.Colors.Background.White};
  cursor: pointer;
  transition: all 0.2s ease;

  border-radius: ${({ theme }) => theme.UI.Spacing.Space3};
  border: 2px solid ${({ theme }) => theme.Colors.Neutrals.Light};

  &:hover {
    border: 2px solid ${({ theme }) => theme.Components.Card.ActiveBorder};
    background-color: ${({ theme }) => theme.Colors.Background.White};
  }

  @media ${breakpoints.toS} {
    width: 100%;
  }
`

const StEnvironmentLabel = styled.div<{
  environment: 'local' | 'dev' | 'acc' | 'prd' | 'qa'
}>`
  position: absolute;
  bottom: 0;
  left: 0;
  background: ${({ theme, environment }) =>
    environment === 'acc' ? theme.Colors.Status.Warning : theme.Colors.Status.Error};
  color: white;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space1}
    ${({ theme }) => theme.UI.SpacingPx.Space4};
  border-radius: 0 ${({ theme }) => theme.UI.SpacingPx.Space2} 0
    ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const StHospitalCardImage = styled.img`
  max-height: 60px;
  max-width: 100%;
  object-fit: contain;
`

const StHospitalSelectionTitle = styled.h2`
  align-self: flex-start;
  width: 100%;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space9} 0px
    ${({ theme }) => theme.UI.SpacingPx.Space4} 0px;

  font-size: ${({ theme }) => theme.Typography.Heading2['font-size']};
  line-height: ${({ theme }) => theme.Typography.Heading2['line-height']};
  font-weight: ${({ theme }) => theme.Typography.Heading2['font-weight']};
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
`
